import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { request } from "app/network";
import { getStoredAccessToken } from "Shared/Utils/CredentialStorage";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

interface AddEditFAQModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode: boolean;
    faq: any | null;
}

interface AddEditFAQModalState {
    categoryCode: string;
    categoryGroupCode: string;
    question: string;
    answer: string;
    isActive: boolean;
    submitting: boolean;
    categories: Array<{ code: string; name: string }>;
    loadingCategories: boolean;
    tenantSeqId: number | null;
    displayOrder: number;
    existingFaqs: Array<{ seqId: number; question: string; displayOrder: number }>;
    newFaqPosition: number;
}

class AddEditFAQModal extends Component<AddEditFAQModalProps, AddEditFAQModalState> {
    constructor(props: AddEditFAQModalProps) {
        super(props);
        this.state = {
            categoryCode: "",
            categoryGroupCode: "FAQ_CATEGORY",
            question: "",
            answer: "",
            isActive: true,
            submitting: false,
            categories: [],

            loadingCategories: false,
            tenantSeqId: null,
            displayOrder: 1,
            existingFaqs: [],
            newFaqPosition: 0,
        };
    }

    componentDidMount() {
        this.loadCategories();
    }

    loadCategories = async () => {
        try {
            this.setState({ loadingCategories: true });
            const response = await request("CommonCode.getByGroup", {
                pathParams: { groupCode: "FAQ_CATEGORY" },
            });

            const categories = response.data.map((item: any) => ({
                code: item.code,
                name: item.name,
            }));

            this.setState({ categories, loadingCategories: false });
        } catch (error) {
            console.error("카테고리 로드 실패:", error);
            this.setState({ loadingCategories: false });
        }
    };

    loadTenantId = () => {
        try {
            const token = getStoredAccessToken();
            if (token) {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                const payload = JSON.parse(jsonPayload);
                if (payload.tenantSeqId) {
                    this.setState({ tenantSeqId: parseInt(payload.tenantSeqId) });
                }
            }
        } catch (error) {
            console.error("토큰에서 Tenant ID 추출 실패:", error);
        }
    };

    loadExistingFaqs = async () => {
        try {
            const response = await request("Faq.pagedList", {
                paramz: {
                    PageNumber: 1,
                    PageSize: 100,
                    IsActive: true,
                },
            });

            if (response && response.data && response.data.items) {
                const existingFaqs = response.data.items
                    .map((faq: any) => ({
                        seqId: faq.seqId,
                        question: faq.question || "질문 없음",
                        displayOrder: faq.displayOrder || 0,
                    }))
                    .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

                this.setState({
                    existingFaqs,
                    newFaqPosition: existingFaqs.length,
                });
            }
        } catch (error) {
            console.error("기존 FAQ 목록 불러오기 실패:", error);
        }
    };

    componentDidUpdate(prevProps: AddEditFAQModalProps) {
        if (this.props.open && !prevProps.open) {
            this.loadTenantId();
            this.loadExistingFaqs();

            if (this.props.faq) {
                this.setState({
                    categoryCode: this.props.faq.categoryCode || "",
                    question: this.props.faq.question || "",
                    answer: this.props.faq.answer || "",
                    isActive: this.props.faq.isActive !== undefined ? this.props.faq.isActive : true,
                    displayOrder: this.props.faq.displayOrder || 1,
                });
            } else {
                this.setState({
                    categoryCode: "",
                    question: "",
                    answer: "",
                    isActive: true,
                    displayOrder: 1,
                    newFaqPosition: this.state.existingFaqs.length,
                });
            }
        }
    }

    handleSubmit = async () => {
        const { categoryCode, categoryGroupCode, question, answer, isActive, displayOrder } = this.state;

        // 유효성 검사
        if (!categoryCode) {
            alert("카테고리를 선택해주세요.");
            return;
        }
        if (!question.trim()) {
            alert("질문을 입력해주세요.");
            return;
        }
        if (!answer.trim()) {
            alert("답변을 입력해주세요.");
            return;
        }

        try {
            this.setState({ submitting: true });

            const payload = {
                CategoryCode: categoryCode,
                CategoryGroupCode: categoryGroupCode,
                Question: question.trim(),
                Answer: answer.trim(),
                IsActive: isActive,
                TenantSeqId: this.state.tenantSeqId,
                DisplayOrder: this.props.faq ? displayOrder : (this.state.newFaqPosition + 1),
            };

            if (this.props.faq) {
                // 수정
                await request("Faq.update", {
                    pathParams: { id: this.props.faq.seqId.toString() },
                    paramz: payload,
                });
            } else {
                // 추가
                await request("Faq.create", {
                    paramz: payload,
                });
            }

            this.setState({ submitting: false });
            this.props.onSuccess();
            this.props.onClose();
        } catch (error: any) {
            console.error("FAQ 저장 실패:", error);
            alert(error?.response?.data?.message || "FAQ 저장에 실패했습니다.");
            this.setState({ submitting: false });
        }
    };

    render() {
        const { open, onClose, darkMode, faq } = this.props;
        const { categoryCode, question, answer, isActive, submitting, categories, loadingCategories, displayOrder } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>
                    <MDTypography variant="h5" fontWeight="medium">
                        {faq ? "FAQ 수정" : "FAQ 추가"}
                    </MDTypography>
                </DialogTitle>
                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                        카테고리 *
                                    </InputLabel>
                                    <Select
                                        value={categoryCode}
                                        label="카테고리 *"
                                        onChange={(e) => this.setState({ categoryCode: e.target.value })}
                                        disabled={loadingCategories}
                                        sx={{
                                            height: '40px',
                                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                            color: darkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
                                            },
                                        }}
                                    >
                                        {categories.map((category) => (
                                            <MenuItem key={category.code} value={category.code}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="질문"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={question}
                                    onChange={(e) => this.setState({ question: e.target.value })}
                                    required
                                    InputLabelProps={{
                                        sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                    }}
                                    InputProps={{
                                        sx: {
                                            color: darkMode ? "#fff" : "#000",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="답변"
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={answer}
                                    onChange={(e) => this.setState({ answer: e.target.value })}
                                    required
                                    InputLabelProps={{
                                        sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                    }}
                                    InputProps={{
                                        sx: {
                                            color: darkMode ? "#fff" : "#000",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {this.props.faq ? (
                                    <TextField
                                        label="순서"
                                        type="number"
                                        fullWidth
                                        value={displayOrder}
                                        onChange={(e) => this.setState({ displayOrder: parseInt(e.target.value) || 0 })}
                                        InputLabelProps={{
                                            sx: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                        }}
                                        InputProps={{
                                            sx: {
                                                color: darkMode ? "#fff" : "#000",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                                                },
                                            },
                                        }}
                                    />
                                ) : this.state.existingFaqs.length > 0 ? (
                                    <DisplayOrderManager
                                        existingFaqs={this.state.existingFaqs}
                                        newFaqPosition={this.state.newFaqPosition}
                                        onPositionChange={(newPosition) =>
                                            this.setState({ newFaqPosition: newPosition })
                                        }
                                        darkMode={darkMode}
                                    />
                                ) : (
                                    <MDBox
                                        p={2}
                                        sx={{
                                            borderRadius: "8px",
                                            backgroundColor: darkMode
                                                ? "rgba(73, 163, 241, 0.1)"
                                                : "rgba(25, 118, 210, 0.05)",
                                            border: darkMode
                                                ? "1px dashed rgba(73, 163, 241, 0.5)"
                                                : "1px dashed rgba(25, 118, 210, 0.5)",
                                        }}
                                    >
                                        <MDTypography
                                            variant="button"
                                            sx={{
                                                color: darkMode ? "#49a3f1" : "#1976d2",
                                            }}
                                        >
                                            🎉 첫 번째 FAQ가 됩니다!
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} display="flex" alignItems="center">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isActive}
                                            onChange={(e) => this.setState({ isActive: e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <MDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                                            활성 상태
                                        </MDTypography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} variant="outlined" color="dark" disabled={submitting}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} variant="gradient" color="info" disabled={submitting}>
                        {submitting ? "저장 중..." : "저장"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

// Sortable Item Component
interface SortableItemProps {
    id: string;
    question: string;
    isNewFaq: boolean;
    darkMode: boolean;
}

function SortableItem({ id, question, isNewFaq, darkMode }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...(isNewFaq ? listeners : {})}  // Only apply listeners to new FAQ
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: isNewFaq
                    ? darkMode
                        ? "2px dashed #49a3f1"
                        : "2px dashed #1976d2"
                    : darkMode
                        ? "1px solid rgba(255, 255, 255, 0.2)"
                        : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: isNewFaq
                    ? darkMode
                        ? "rgba(73, 163, 241, 0.1)"
                        : "rgba(25, 118, 210, 0.05)"
                    : darkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                cursor: isNewFaq ? "grab" : "default",  // Only new FAQ is draggable
                "&:active": {
                    cursor: isNewFaq ? "grabbing" : "default",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: isNewFaq
                        ? darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)"
                        : darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight={isNewFaq ? "bold" : "regular"}
                sx={{
                    color: isNewFaq
                        ? darkMode
                            ? "#49a3f1"
                            : "#1976d2"
                        : darkMode
                            ? "rgba(255, 255, 255, 0.8)"
                            : "#344767",
                }}
            >
                {isNewFaq ? "🆕 " : ""}{question}
            </MDTypography>
        </MDBox>
    );
}

// Display Order Manager Component
interface DisplayOrderManagerProps {
    existingFaqs: Array<{ seqId: number; question: string; displayOrder: number }>;
    newFaqPosition: number;
    onPositionChange: (newPosition: number) => void;
    darkMode: boolean;
}

function DisplayOrderManager({
    existingFaqs,
    newFaqPosition,
    onPositionChange,
    darkMode,
}: DisplayOrderManagerProps) {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Create items array with new FAQ placeholder
    const items = [
        ...existingFaqs.slice(0, newFaqPosition).map((faq) => ({
            id: `existing-${faq.seqId}`,
            question: faq.question,
            isNewFaq: false,
        })),
        {
            id: "new-faq",
            question: "새 FAQ",
            isNewFaq: true,
        },
        ...existingFaqs.slice(newFaqPosition).map((faq) => ({
            id: `existing-${faq.seqId}`,
            question: faq.question,
            isNewFaq: false,
        })),
    ];

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id && active.id === "new-faq") {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            // Use arrayMove to get the new order
            const reorderedItems = arrayMove(items, oldIndex, newIndex);

            // Find the new position of "new-faq" in the reordered array
            const newFaqIndexInReordered = reorderedItems.findIndex((item) => item.id === "new-faq");

            // Count how many existing FAQs are before the new-faq position
            let actualPosition = 0;
            for (let i = 0; i < newFaqIndexInReordered; i++) {
                if (reorderedItems[i].id !== "new-faq") {
                    actualPosition++;
                }
            }
            onPositionChange(actualPosition);
        }
    };

    return (
        <MDBox>
            <MDTypography
                variant="caption"
                fontWeight="regular"
                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                mb={1}
            >
                FAQ 표시 순서 (드래그하여 위치 조정)
            </MDTypography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableItem
                            key={item.id}
                            id={item.id}
                            question={item.question}
                            isNewFaq={item.isNewFaq}
                            darkMode={darkMode}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <MDTypography
                variant="caption"
                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)" }}
                display="block"
                mt={1}
            >
                💡 새 FAQ를 드래그하여 원하는 위치에 배치하세요
            </MDTypography>
        </MDBox>
    );
}

export default AddEditFAQModal;
